<script lang="ts" setup>
    import '../../../css/choice.css';  
    import { ModalWindowOpen } from '../../lib/ChoiceUserConfiguration';
    import {onMounted,onBeforeMount,computed,reactive} from 'vue';  
    import {popAlert,ModalStyles} from '../../lib/Notification';     
    import {AppScreens} from '../../Enums/ChoiceEnums';
    import {ButtonDisplayStyle,ButtonDisplaySize,DropDownButtonIconType} from "../../Enums/UiEnums"; 
    import {ErrorReturnMessage,getRefundRegister,createRefundTransaction,updateRefundTransaction} from '../../lib/Api/RefundRegister';
    import {EntityType,TransactionStatus,IClaimRegisterList,ITransactionCreateRequest,ITransactionUpdateRequest,IRefundRegisterResponse,UpdateTitleBar,RefundColumnDefinitions} from '../../lib/Models/RefundRegisterModel'; 
    import {ClaimSimpleLists,claimLegacyWindowOpen} from '../../lib/Models/ClaimModel';
    import {prettyCurrency} from '../../lib/Formatting/Number'; 
    import ModalDialog from '../../components/ModalDialog.vue'; 
    import SimpleList from '../../components/SimpleList.vue';
    import TextInput from '../../components/TextInput.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import DropDownButton from "../../components/DropDownButton.vue";
    import DropDownButtonItem from "../../components/DropDownButtonItem.vue";
    import {ActionButtonStyle} from '../../lib/Types/Types';   
    import { initPinia } from '../../lib/PiniaMgr';
    import {useSimpleListStore} from '../../lib/Stores/SimpleListStore';
    
    enum PageActions{
        Audit,
        Refresh 
    } 
    const errorMessage1 = "Check that you have selected at least 1 claim and entered a transaction number.";
    const errorMessage2 = "Transaction number is required.";
    const simpleListStore = useSimpleListStore(initPinia());
    
    onBeforeMount(async () => {
        await simpleListStore.getSimpleLists([ClaimSimpleLists.RefundPaymentType, ClaimSimpleLists.PaymentMethod]);  
    });
    const formData = reactive <
    {   
        validIdentifier: boolean,
        registerIdentifier: string,  
        refundResponse?: IRefundRegisterResponse,
        patientFound: boolean,
        pendingTransaction: boolean,
        window: {headerHeight: number, footerHeight: number, windowPadding: number, workareaHeight: number}, 
        buttonDisplay: {add: boolean, update: boolean, cancel: boolean, complete: boolean},
        EntityTypeDescription: string,
        listKey: number,
        showCancelled: boolean,
        showCompleted: boolean,
        allClaimSaved: boolean
    }>({      
        validIdentifier: false,
        registerIdentifier: "",
        patientFound: false,
        pendingTransaction: false,
        window: {headerHeight: 19.5, footerHeight: 4.5, windowPadding: 3.5, workareaHeight: 7},
        buttonDisplay: {add: false, update: false, cancel: false, complete: false},
        EntityTypeDescription: "",
        listKey: 0,
        showCancelled: false,
        showCompleted: false,
        allClaimSaved: false
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        registerIdentifier : string
    }>(),
    {
        saveEnabled: 0,  
        registerIdentifier: "0"
    });
    
    const typeListLoaded = computed<boolean>(()=> {
        if (!simpleListStore) {
            return false;
        } 
        if (!simpleListStore.listResult[ClaimSimpleLists.RefundPaymentType]) {
            return false;
        }
        return true;
    });
    const methodListLoaded = computed<boolean>(()=> {
        if (!simpleListStore) {
            return false;
        } 
        if (!simpleListStore.listResult[ClaimSimpleLists.PaymentMethod]) {
            return false;
        } 
        return true;
    });
 
    const screenEditable = computed<boolean>(()=> {
        if (props.saveEnabled < 1) {
            return false;
        }
        return true;
    });
    const saveButtonDisabled = computed<boolean>(()=> {
        return checkBadInputValues(true,true);
    });
    const completeButtonDisabled = computed<boolean>(()=> {
        return checkBadInputValues(false,true);
    });
    const updateButtonDisabled = computed<boolean>(()=> {
        return checkBadInputValues(false,true);
    });
    const cancelButtonDisabled = computed<boolean>(()=> {
        return checkBadInputValues(false,!formData.pendingTransaction);
    });
    function checkBadInputValues(claimListCheck : boolean, checkTransactionNumber : boolean) : boolean {
        if (!formData.refundResponse) {
            return true;
        }
        if (!formData.refundResponse.refundDetail.transactionNumber && checkTransactionNumber) {
            return true
        }
        if (!formData.refundResponse.refundDetail.paymentMethodID) {
            return true
        }
        if (!formData.refundResponse.refundDetail.paymentTypeID) {
            return true
        }
        if (claimListCheck) {
            if (formData.refundResponse.claimList.filter(item => item.selected).length == 0) {
                return true
            }
        }
        return false;
    }

    onMounted(async () => {
        formData.registerIdentifier = props.registerIdentifier;
        if (formData.registerIdentifier != "0") {
            formData.validIdentifier = true;
            await refreshData();
            return;
        }
        formData.validIdentifier = false;
        UpdateTitleBar(props.registerIdentifier,"No Refundee");
    });
    async function refreshData() : Promise<void>  {
        formData.listKey++;
        const respGet = await getRefundRegister(props.registerIdentifier); 
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.details,"Refresh Details",ModalStyles.danger);
            return;
        }  
        formData.refundResponse = respGet.success;
        UpdateTitleBar(formData.registerIdentifier, (formData.refundResponse?.refundDetail.refundeeName ?? ""));
        formData.EntityTypeDescription = (formData.refundResponse.refundDetail.entityType === EntityType.Payer ? "Payer" : "Responsible Party");
        formData.pendingTransaction = (formData.refundResponse.refundDetail.transactionStatus == TransactionStatus.Pending);
        formData.patientFound = (formData.refundResponse.refundDetail.patientID > 0 ? true : false);
        formData.buttonDisplay.add = (screenEditable.value && formData.refundResponse.refundDetail.transactionStatus === TransactionStatus.Pending ? true : false);
        formData.buttonDisplay.cancel = (screenEditable.value && (formData.refundResponse.refundDetail.transactionStatus === TransactionStatus.Pending || formData.refundResponse.refundDetail.transactionStatus === TransactionStatus.Completed) ? true : false);
        formData.buttonDisplay.update = (screenEditable.value && formData.refundResponse.refundDetail.transactionStatus === TransactionStatus.Completed ? true : false);
        formData.buttonDisplay.complete = (screenEditable.value && formData.refundResponse.refundDetail.transactionStatus === TransactionStatus.Cancelled ? true : false);
        if (formData.pendingTransaction) {
            formData.refundResponse.claimList.forEach(function(item) {
                    item.selected = true;
                });
        }
        resetHeights(); 
    } 
    function resetHeights(): void { 
        const windowHeight = (window.innerHeight / 16);
        const addressCount = formData.refundResponse?.refundAddressList.length ?? 0;
        const contactCount = formData.refundResponse?.refundContactList.length ?? 0;
        const max_record_count = (contactCount > addressCount ? contactCount : addressCount);
        const additionHeaderHeight = max_record_count * 20 / 16;
        formData.window.headerHeight = 12.5 + additionHeaderHeight + (formData.patientFound ? 2 : 0);
        formData.window.workareaHeight = windowHeight - formData.window.headerHeight - formData.window.footerHeight - formData.window.windowPadding;
    }
    async function closeRegister() : Promise<void> {
        if (parent.location.href.indexOf("tablesearch.htm") != -1) {
            (parent as any).WindowFrameDisplay(false);
        }
        else {
            window.close();
        } 
    }
    async function saveRegister() : Promise<void> {
        await saveRegisterEx(TransactionStatus.Completed);
    }
    async function saveRegisterEx(newTransactionStatus : TransactionStatus) : Promise<void> {
        if (!formData.refundResponse) {
            return;
        }  
        const claim_list : IClaimRegisterList[] = formData.refundResponse.claimList.filter(item => item.selected).map(item => ({claimRegisterID: item.id}));
        formData.allClaimSaved = (claim_list.length == formData.refundResponse.claimList.length);
        const save_record : ITransactionCreateRequest =
        {
            refundEntityID: formData.refundResponse.refundDetail.refundEntityID,
            paymentMethodID: +formData.refundResponse.refundDetail.paymentMethodID,
            paymentTypeID: +formData.refundResponse.refundDetail.paymentTypeID,
            transactionNumber: formData.refundResponse.refundDetail.transactionNumber,
            transactionStatus: newTransactionStatus,
            registerList: claim_list
        }
        const returnResults = await createRefundTransaction(save_record);
        const messageTitle = "Save Refund Register";
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? ErrorReturnMessage.create,messageTitle,ModalStyles.danger);
            return;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return;
        }        
        if (formData.allClaimSaved) {
            closeRegister();
            if (parent.location.href.indexOf("tablesearch.htm") != -1) {
                (parent as any).RefreshLastSearch();
            }
        }
        else {
            await refreshData();
        }
    }
    function returnUpdateRecord(newStatus : TransactionStatus) : ITransactionUpdateRequest | undefined {
        if (!formData.refundResponse) {
            return;
        }        
        const update_record : ITransactionUpdateRequest =
        {
            transactionRegisterID: formData.refundResponse.refundDetail.transactionRegisterID,
            transactionStatus: newStatus,
            paymentMethodID: +formData.refundResponse.refundDetail.paymentMethodID,
            paymentTypeID: +formData.refundResponse.refundDetail.paymentTypeID,
            transactionNumber: formData.refundResponse.refundDetail.transactionNumber
        }
        return update_record;
    }    
    async function cancelRegister() : Promise<void> {
        formData.showCancelled = true;
    }
    async function executeCancelRegister() : Promise<void> { 
        if (!formData.refundResponse) {
            return;
        }
        if (formData.pendingTransaction) {
            if (!formData.refundResponse?.refundDetail.transactionNumber) {
                formData.refundResponse.refundDetail.transactionNumber = "Cancelled"
            }
            await saveRegisterEx(TransactionStatus.Cancelled);
            formData.showCancelled = false;
            return;
        }
        const update_record = returnUpdateRecord(TransactionStatus.Cancelled);
        if (!update_record) {
            return;
        }
        const update_return = await executeUpdateRegister(update_record,"Cancel Refund Register",ErrorReturnMessage.cancel);
        if (update_return) {
            await refreshData();
            formData.showCancelled = false;
        }
    }
    async function completeRegister() : Promise<void> {
        formData.showCompleted = true;
    }
    async function executeCompleteRegister() : Promise<void> {
        const update_record = returnUpdateRecord(TransactionStatus.Completed);
        if (!update_record) {
            return;
        }
        const update_return = await executeUpdateRegister(update_record,"Complete Refund Register",ErrorReturnMessage.complete);
        if (update_return) {
            await refreshData();
            formData.showCompleted = false;
        }
    }
    async function updateRegister() : Promise<void> {
        const update_record = returnUpdateRecord(TransactionStatus.Completed);
        if (!update_record) {
            return;
        }
        const update_return = await executeUpdateRegister(update_record,"Update Refund Register",ErrorReturnMessage.update);
        if (update_return) {
            await refreshData();
        }
    }
    async function executeUpdateRegister(updateRecord : ITransactionUpdateRequest, messageTitle : string, defaultErrorMessage: ErrorReturnMessage) : Promise<boolean> {
        const returnResults = await updateRefundTransaction(updateRecord);
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? defaultErrorMessage,messageTitle,ModalStyles.danger);
            return false;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return false; 
        }        
        return true;
    }
    function onChangePaymentMethod(selectedItem : any) : void {
        if (!formData.refundResponse) {
            return;
        }
        formData.refundResponse.refundDetail.paymentMethodID = selectedItem.value;
    }
    function onChangePaymentType(selectedItem : any) : void {
        if (!formData.refundResponse) {
            return;
        }
        formData.refundResponse.refundDetail.paymentTypeID = selectedItem.value;
    }
    
    function openClaim(claimId : number) : void {
        if (!claimId) {  
            return;
        } 
        const claimURL = `/officemd/screens/ClaimEntry.htm?ClaimID=${claimId}`;     
        claimLegacyWindowOpen("ClaimEntry", claimURL);
    }
    function openPatient() : void  { 
        if (!formData.refundResponse?.refundDetail.patientID) {  
            return;
        } 
        const patientURL = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${formData.refundResponse.refundDetail.patientID}&tab=Demographics`;
        claimLegacyWindowOpen("NewPatient", patientURL); 
    } 
    function displayAudit() : void  {
        const statement_id = formData.refundResponse?.refundDetail.transactionRegisterID ?? 0;
        ModalWindowOpen({screenID: AppScreens.Logging, urlParmData: {ScreenName: "RefundRegister.cshtml", LogType: "s", StatementId: statement_id}, title: "Refund Regiseter Audit"}); 
    }
</script> 

<style scoped> 
.billing-header {
  color: #204D89;
  background-color: #f1f1f1;
}
.billing-data-left {
  color: black;
  background-color: white;
  text-align: left;
}    
.billing-data-center {
  color: black;
  background-color: white;
  text-align: center;
}
.billing-data-right {
  color: black;
  background-color: white;
  text-align: right;
} 
.billing-text {
  FONT-SIZE: 8pt;
  font-family: Arial,  sans-serif, Helvetica;
  padding: 5px 5px 5px 5px;
  cursor: default;
  vertical-align: middle;
}
.billing-reversal { 
    text-decoration: line-through; 
}
.billing-break {
    border-top: 1px solid silver;
}    
.billing-cell {
      border: 1px solid #6A8CCB;
      FONT-SIZE: 8pt;
      font-family: Arial, sans-serif, Helvetica;
      padding: 5px 5px 5px 5px;
      cursor: default;
      vertical-align: middle;
    }
</style>
<template>
    <div v-if="!formData.validIdentifier" class="row" >
        <div class="col text-danger">
           Invalid Transaction Identifier, no data found.
        </div>
    </div>

    <div v-if="formData.validIdentifier" class="row">
        <div class="col-6 fw-bold label-isalus-small text-isalus-primary pb-3">
            Register Information:
        </div>
        <div class="col-6 text-end pe-3">
            <DropDownButton :display-style="ButtonDisplayStyle.Link" :buttonIcon="DropDownButtonIconType.Gear" >
                <DropDownButtonItem v-if="!formData.pendingTransaction" style="font-size: .75rem;" :data="PageActions.Audit" @click="displayAudit">Audit</DropDownButtonItem>
                <DropDownButtonItem style="font-size: .75rem;" :data="PageActions.Refresh" @click="refreshData">Refresh</DropDownButtonItem> 
            </DropDownButton>
        </div>
    </div>

    <div v-if="formData.validIdentifier" class="row skinnyscroll" :style="{ 'overflow-y': 'hidden', 'overflow-x': 'hidden',  'height': (formData.window.headerHeight + 'rem')}">
        <div class="col">
            <table v-if="formData.refundResponse?.refundDetail" border="0" aria-hidden="true" :style="`table-layout: fixed; border-collapse: collapse;  width: 41rem;`">
                <colgroup>
                    <col :style="`width: ${RefundColumnDefinitions.Column1}rem;`"/>
                    <col :style="`width: ${RefundColumnDefinitions.Column2}rem;`"/>
                    <col :style="`width: ${RefundColumnDefinitions.Column3}rem;`"/>
                    <col :style="`width: ${RefundColumnDefinitions.Column4}rem;`"/> 
                </colgroup>
                <tr>
                    <td class="billing-cell billing-header">ID</td>
                    <td class="billing-cell billing-data-left">{{props.registerIdentifier}}</td>
                    <td class="billing-cell billing-header">Refundee</td>
                    <td class="billing-cell billing-data-left">{{formData.refundResponse.refundDetail.refundeeName}} - {{formData.EntityTypeDescription}}</td>
                </tr>
                <tr>
                    <td class="billing-cell billing-header">Status</td>
                    <td class="billing-cell billing-data-left">{{formData.refundResponse.refundDetail.transactionStatusDescription}}</td>
                    <td class="billing-cell billing-header">Transaction Number</td>
                    <td class="billing-cell">
                        <TextInput 
                            v-model:textValue="formData.refundResponse.refundDetail.transactionNumber" 
                            :cssFramework="'vanilla'"  
                            placeholder="Transaction Number"
                        />
                    </td>                     
                </tr>
                <tr>
                    <td class="billing-cell billing-header">Date</td>
                    <td class="billing-cell billing-data-left">{{formData.refundResponse.refundDetail.transactionDate}}</td>      
                    <td class="billing-cell billing-header">Payment Method</td>
                    <td class="billing-cell">
                        <SimpleList v-if="methodListLoaded"
                            :listName="ClaimSimpleLists.PaymentMethod"
                            :includeHeader="true"
                            :includeBlank="false" 
                            :disabled="!screenEditable" 
                            v-model:selected="formData.refundResponse.refundDetail.paymentMethodID"
                            @onChange="onChangePaymentMethod" 
                        />
                    </td>                                   
                </tr>
                <tr>
                    <td class="billing-cell billing-header">Amount</td>
                    <td class="billing-cell billing-data-left">{{prettyCurrency(formData.refundResponse.refundDetail.paymentAmount)}}</td>
                    <td class="billing-cell billing-header">Payment Type</td>
                    <td class="billing-cell">
                        <SimpleList v-if="typeListLoaded"
                            :listName="ClaimSimpleLists.RefundPaymentType"
                            :includeHeader="true"
                            :includeBlank="false"
                            :disabled="!screenEditable" 
                            v-model:selected="formData.refundResponse.refundDetail.paymentTypeID"
                            @onChange="onChangePaymentType" 
                        />   
                    </td>                    
                </tr>
                <tr v-if="formData.patientFound">
                    <td class="billing-cell billing-header">Patient</td>
                    <td class="billing-cell billing-data-left" colspan="3">
                        <span @click="openPatient" class="label-isalus-small isalus-link">
                            {{formData.refundResponse?.refundDetail.patientID}}
                        </span>
                            <span class="ps-2 label-isalus-small">
                            {{formData.refundResponse?.refundDetail.patientName}}
                        </span>
                    </td>
                </tr>
            </table>
            <br/>
            <table>
                <tr class="fw-bold label-isalus-small text-isalus-primary">
                    <td class="pe-2" style="border-right: 1pt solid #6A8CCB;">Contacts:</td>
                    <td class="ps-2">Addresses:</td>
                </tr>
                <tr>
                    <td class="align-top label-isalus-small pe-2" style="border-right: 1pt solid #6A8CCB;">
                        <table>
                            <tr v-if="(formData.refundResponse?.refundContactList.length ?? 0) > 0" v-for="(contactRecord,index) in formData.refundResponse?.refundContactList">   
                                <td class="ps-2">{{contactRecord.contactName}}</td>
                                <td class="ps-2" :title="`Description: ${contactRecord.contactDescription}\r\nNumber: ${contactRecord.contactNumber}`">{{contactRecord.contactNumber}}</td>
                            </tr>
                        </table>
                    </td>
                    <td class="ps-2 align-top label-isalus-small">
                        <table>
                            <tr v-if="(formData.refundResponse?.refundAddressList.length ?? 0) > 0" v-for="(addressRecord,index) in formData.refundResponse?.refundAddressList">   
                                <td class="ps-2">{{addressRecord.fullAddress}} ({{addressRecord.description}})</td>
                            </tr>
                        </table>
                    </td>
                </tr>
            </table>
            <hr/>
        </div>
    </div>
    <div class="row skinnyscroll" :style="{'overflow-y': 'scroll', 'overflow-x': 'hidden', 'height': (formData.window.workareaHeight + 'rem')}">  
        <div class="col">
            <table>
                <tr>
                    <td :colspan="(formData.pendingTransaction ? 7 : 6)" class="fw-bold label-isalus-small text-isalus-primary pb-3">Claim Register Details:</td>
                </tr>
                <tr>
                    <td v-if="formData.pendingTransaction" class="billing-cell billing-header">#</td>
                    <td class="billing-cell billing-header">ID</td>
                    <td class="billing-cell billing-header">Claim</td>
                    <td class="billing-cell billing-header">Refundee</td>
                    <td class="billing-cell billing-header">Amount</td>
                    <td class="billing-cell billing-header">Payment Type</td>
                    <td class="billing-cell billing-header">Logging</td>
                </tr>
                <tr v-if="(formData.refundResponse?.claimList.length ?? 0) > 0" v-for="(claimRecord,index) in formData.refundResponse?.claimList" :key="`Key${formData.listKey}CLM${claimRecord.id}`"> 
                   
                    <td v-if="formData.pendingTransaction" class="billing-cell billing-data-center">
                        <Checkbox v-model="claimRecord.selected"/>
                    </td>  
                    <td class="billing-cell billing-data-left">{{claimRecord.id}}</td>  
                    <td class="billing-cell billing-data-left">
                        <span @click="openClaim(claimRecord.claimID)" class="isalus-link">
                            {{claimRecord.claimID}}
                        </span>
                    </td>  
                    <td class="billing-cell billing-data-left">{{claimRecord.refundeeName}}</td>  
                    <td class="billing-cell billing-data-right">{{prettyCurrency(claimRecord.refundAmount)}}</td>  
                    <td class="billing-cell billing-data-left">{{claimRecord.paymentTypeDescription}}</td>  
                  
                    <td colspan="2" class="billing-cell billing-data-left">
                        <div>
                            {{claimRecord.createdBy}}
                        </div>
                        <div v-if="!formData.pendingTransaction">
                            {{claimRecord.transactedBy}}
                        </div>
                    </td>
                </tr>
            </table>
        </div>
    </div>
    <div class="row" :style="{'overflow': 'hidden', 'height': (formData.window.footerHeight + 'rem')}">
        <hr/>     
        <div class="col-6 text-danger label-isalus-small">
            <span v-if="formData.buttonDisplay.add && saveButtonDisabled">{{errorMessage1}}</span>
            <span v-if="(formData.buttonDisplay.update && updateButtonDisabled) || (formData.buttonDisplay.cancel && cancelButtonDisabled) || (formData.buttonDisplay.complete && completeButtonDisabled)">{{errorMessage2}}</span>
        </div>
        <div class="col-6 text-end">
            <span v-if="formData.buttonDisplay.add" class="pe-2">
                <ActionButtonV2
                    :action="ActionButtonStyle.Add"
                    :disabled="saveButtonDisabled"
                    :text="'Save'"
                    :tooltip="'Create new Refund Registry record'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :performAction="saveRegister"
                />
            </span>
            <span v-if="formData.buttonDisplay.update" class="pe-2">
                <ActionButtonV2
                    :action="ActionButtonStyle.Save"
                    :text="'Update'"
                    :disabled="updateButtonDisabled"
                    :tooltip="'Update registry information (Transaction Number, Payment Method and/or Payment Type)'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Update'"
                    :performAction="updateRegister"
                />
            </span>
            <span v-if="formData.buttonDisplay.cancel" class="pe-2">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Erase"
                    :text="'Cancel Refund'"
                    :disabled="cancelButtonDisabled"
                    :tooltip="'Cancel Refund Registry'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Cancel Refund'"
                    :performAction="cancelRegister"
                />
            </span>
            <span v-if="formData.buttonDisplay.complete" class="pe-2">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Save"
                    :text="'Complete'"
                    :disabled="completeButtonDisabled"
                    :tooltip="'Change status from Cancelled to Completed'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Complete'"
                    :performAction="completeRegister"
                />
            </span>
            <span class="pe-2">
                <ActionButtonV2
                    :action="ActionButtonStyle.Close"
                    :text="'Close'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :tooltip="'Close window'"
                    :spinText="'Close'"
                    :performAction="closeRegister"
                />
            </span>
        </div>
    </div>
    <ModalDialog v-model:show="formData.showCancelled">
        <template #title>
            <div class="text-isalus-primary">Cancel Refund Registry</div>
        </template>
        <div class="label-isalus-small text-isalus-primary fw-bold pb-2">
            Please note:
        </div>
        <div class="label-isalus-small text-isalus-primary ps-2 pb-2">
            When Cancelling a Refund Registry (Changing the status from Pending or Completed to Cancelled), you will need to adjust the recorded refund payment for each claim within the refund registry.
        </div>
        <div class="label-isalus-small text-danger">
            This process will NOT automatically perform this task for you. It is only updating the registry record status.
        </div>
        <template #footer> 
            <span class="text-end">
                <ActionButtonV2
                    :action="ActionButtonStyle.Save"
                    :text="'Continue'" 
                    :tooltip="'Continue Cancelling Registry record'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Cancelling Registry'"
                    :performAction="executeCancelRegister"
                />
            </span>
        </template>
    </ModalDialog>   
    <ModalDialog v-model:show="formData.showCompleted">
        <template #title>
            <div class="text-isalus-primary">Complete Refund Registry</div>
        </template>
        <div class="label-isalus-small text-isalus-primary fw-bold pb-2">
            Please note:
        </div>
        <div class="label-isalus-small text-isalus-primary ps-2 pb-2">
            When Completing a Refund Registry (Changing the status from Cancelled to Completed), you will need to adjust the recorded refund payment for each claim within the refund registry.
        </div>
        <div class="label-isalus-small text-danger">
            This process will NOT automatically perform this task for you. It is only updating the registry record status.
        </div>
        <template #footer> 
            <span class="text-end">
                <ActionButtonV2
                    :action="ActionButtonStyle.Save"
                    :text="'Continue'" 
                    :tooltip="'Continue Completing Registry record'"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :spinText="'Completing Registry'"
                    :performAction="executeCompleteRegister"
                />
            </span>
        </template>
    </ModalDialog>    
</template>
